import isEqual from 'lodash/isEqual';

import { LONG_REQUEST_TIMEOUT } from '../constants';
import { timeoutPromise } from '../utils/utils';

const MAX_FAILED_REQUESTS = 3;

export interface PeriodicRequestProps<T> {
  /**
   * Time, in ms, between requests.
   */
  interval: number;

  /**
   * Time, in ms, to wait before timing out onPeriodicRequest(). Defaults to LONG_REQUEST_TIMEOUT.
   */
  timeout?: number;

  /**
   * Always invoke the 'onPeriodicRequestResult' callback, regardless of whether or not
   * data from the server is different from the previous result.
   */
  neverCheckEquality?: boolean;

  /**
   * Called periodically according to interval.
   * The result will be passed to onPeriodicRequestResult if it's truthy and different from the previous result.
   */
  onPeriodicRequest(): Promise<T>;

  /**
   * Called with the result of onPeriodicRequest().
   */
  onPeriodicRequestResult(value: NonNullable<T>): void;
}

/**
 * Helper class to execute requests at fixed intervals.
 * Can be used in controllers to periodically fetch data from the server.
 */
export class PeriodicRequest<T> {
  private requestCounter = 0;

  private lastAppliedRequest = 0;

  private lastResponse: T | undefined;

  private timeout: number;

  private failCount = 0;

  constructor(private props: PeriodicRequestProps<T>) {
    this.timeout = this.props.timeout ?? LONG_REQUEST_TIMEOUT;
  }

  /**
   * Stop executing periodic requests.
   */
  stop: () => void = () => {};

  /**
   * Start executing periodic requests.
   *
   * @param immediately if true (the default), send a request immediately. Otherwise wait for the next interval.
   * @returns this.stop
   */
  start(immediately = true) {
    // counter to force periodic requests to be in-order (out-of-order calls are discarded)

    const token = setInterval(() => void this.refresh(), this.props.interval);
    if (immediately) {
      void this.refresh();
    }

    this.stop = () => clearInterval(token);

    return this.stop;
  }

  /**
   * Force a periodic request to run now.
   */
  refresh = async (): Promise<void> => {
    const requestNumber = ++this.requestCounter;

    try {
      const response = await Promise.race([this.props.onPeriodicRequest(), timeoutPromise(this.timeout)]);

      if (!response || this.lastAppliedRequest > requestNumber) {
        // more recent data has already been applied, discard this call
        return;
      }

      this.lastAppliedRequest = requestNumber;

      const shouldIgnore = !this.props.neverCheckEquality && isEqual(this.lastResponse, response);
      if (shouldIgnore) {
        return;
      }

      this.lastResponse = response;
      this.props.onPeriodicRequestResult(response);
    } catch (error) {
      if (this.failCount >= MAX_FAILED_REQUESTS) {
        this.stop();
        console.error('Error occurred during periodic request. Stopping interval due too many failures: ', this.failCount);
      } else {
        this.failCount++;
        console.warn('Error occurred during periodic request: ', error);
      }
    }
  };
}
